import {
	LightbulbLit,
	LogoText2,
	NavigationalLeftArrow
} from '@/resources/assets/images';
import Button from '@/components/Button/newButtons';
import { ErrorMessage } from '@/components/atoms/ErrorMessage';
import { useState } from 'react';
import { sendAnalytics } from '@/services/analyticsService';
import { RootState } from '@/rootReducer';
import { useSelector } from 'react-redux';
import { useOnboardingContext } from '../context/OnboardingContext';

export default function OnboardingValue() {
	const { onboardingData, setOnboardingData, nextPage, previousPage } =
		useOnboardingContext();
	const [valueError, setValueError] = useState<string | null>(null);
	const user = useSelector((state: RootState) =>
		state.user.getIn(['authorize', 'user']).toJS()
	);

	const handleNextClick = () => {
		const length = onboardingData.valueProposition?.length ?? 0;

		if (length < 1) {
			setValueError(
				'Please fill in the value proposition before continuing.'
			);
			return;
		}

		if (length > 1000) {
			setValueError(
				'Your value proposition must be under 1000 characters.'
			);
			return;
		}

		setValueError(null);
		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: 'Onboarding Flow - Submitted value prop',
			properties: {
				page: 'Step 3: Value Page',
				valueProposition: onboardingData.valueProposition
			}
		});
		nextPage();
	};

	const variant =
		user?.features?.onboardingTestGroup === 'test'
			? 'OnboardingFlowA'
			: 'OnboardingFlowB';

	const getCopy = () => {
		if (variant === 'OnboardingFlowA') {
			return {
				title: 'What makes your offer a no-brainer?',
				body: (
					<>
						<span className="font-barlow text-base font-normal leading-8 text-dm-white sm:leading-8">
							This is the hook.{' '}
							<span className="font-bold text-dm-gold">
								The outcome.
							</span>{' '}
							The thing that grabs attention and gets someone to
							say &quot;I need that.&quot; We&apos;ll build your
							campaign around it.
						</span>
					</>
				)
			};
		}

		// Variant B (default)
		return {
			title: 'Why should they care?',
			body: (
				<>
					<span className="font-barlow text-base font-normal leading-8 text-dm-white sm:leading-8">
						Tell us &quot;why now?&quot; that gets replies. <br />
						Give us the{' '}
						<span className="font-bold text-dm-gold">
							one benefit that matters most
						</span>{' '}
						— we&apos;ll take care of the rest.
					</span>
				</>
			)
		};
	};

	const copy = getCopy();

	return (
		<div className="h-full w-full overflow-hidden">
			{/* Top Header */}
			<div className="h-[3.75rem] w-full overflow-hidden rounded-tl-lg rounded-tr-lg border-b border-t border-dm-gray px-4 sm:h-[6.375rem] sm:px-20 lg:rounded-tr-none lg:border-l">
				<div className="flex h-full w-full items-center">
					<div className="mx-auto flex w-full max-w-[79rem] items-center justify-between">
						<div className="flex items-center gap-4">
							<LightbulbLit className="h-[2rem] w-[1.375rem] shrink-0" />
							<h1 className="font-barlow text-lg font-semibold leading-[1.375rem] text-dm-white">
								Step 3 of 4: Your Value Prop
							</h1>
						</div>

						<LogoText2 className="h-6 w-[6.5625rem] transition-all duration-300 3xl:-mr-[3.75rem]" />
					</div>
				</div>
			</div>

			{/* Main Content */}
			<div className="h-full border-dm-gray px-4 py-6 sm:py-10 sm:pl-12 sm:pr-16 lg:border-l">
				<div className="mx-auto flex min-h-[25.563rem] w-full max-w-[79rem] flex-col overflow-hidden rounded-2xl border border-dm-gray bg-dm-black3/50 px-4 pt-8 sm:px-20">
					<div className="flex flex-1 flex-col gap-2 overflow-auto pb-8 sm:gap-10 sm:overflow-visible sm:pb-0">
						<Button
							type="button"
							variant="text"
							className="flex rounded-lg p-0 text-dm-white sm:hidden"
							onClick={previousPage}
							leftIcon={<NavigationalLeftArrow />}
						>
							Back
						</Button>
						<div className="flex flex-col gap-4">
							{/* Step Title */}
							<span className="font-barlow text-xl font-semibold leading-8 tracking-normal text-dm-white sm:text-2xl sm:leading-10">
								{copy.title}
							</span>

							{/* Body Text */}
							<div className="max-w-[41rem] font-barlow text-sm font-normal leading-7 text-dm-white sm:text-base sm:leading-8">
								{copy.body}
							</div>
							<div className="flex flex-col gap-1">
								{/* Textarea */}
								<textarea
									autoFocus
									placeholder="E.g. We help local salons get 20+ new customers a month through better online presence."
									className="h-[4.75rem] w-full max-w-[41rem] resize-none rounded-[.25rem] border border-gray-300 bg-white px-3 py-2 font-barlow text-sm font-normal leading-[1.188rem] tracking-normal text-black focus:outline-none sm:text-base"
									value={onboardingData.valueProposition}
									onChange={e =>
										setOnboardingData({
											valueProposition: e.target.value
										})
									}
								/>
								<div className="-mt-1 h-[1rem]">
									{valueError && (
										<ErrorMessage message={valueError} />
									)}
								</div>
							</div>
						</div>

						{/* Button */}
						<div className="flex flex-col justify-start gap-7 sm:flex-row sm:gap-14">
							<Button
								type="button"
								variant="secondary"
								className="hidden rounded-lg px-6 text-white sm:flex"
								onClick={previousPage}
							>
								Go Back
							</Button>
							<Button
								type="button"
								smart
								variant="primary"
								className="rounded-lg px-6 text-white"
								onClick={handleNextClick}
							>
								Next: Your Signature
							</Button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
